import { useMemo } from 'react';

import {
  DEFAULT_PAGE_NAMES,
  type PageIdentifier,
} from 'hb-react/shared/constants/pageIdentifiers';
import { usePageNamesQueries } from 'hb-react/shared/hooks/usePageNamesQueries';

import { useTopicId, useGlobalOrTopicMenu } from './hooks';
import { type UsePageMenuNamesOptions } from './types';
import {
  fillUnknownWithEmptyStrings,
  fillUnknownWithDefaultNames,
  getPageNamesFromMenu,
} from './utils';

/**
 * Hook that returns the names of the pages from the menu. It first tries to
 * get the names from the menu, and if they are not found, it fetches them
 * from the page_menu_names endpoint.
 *
 * @param identifiers {Array<PageIdentifier>} The list of page identifiers.
 * @param options {UsePageMenuNamesOptions} Options object.
 * @returns {PageNames} The names of the pages.
 */
export const usePageMenuNames = (
  identifiers: Array<PageIdentifier>,
  {
    topicId: topicIdParam,
    defaultNames = DEFAULT_PAGE_NAMES,
  }: UsePageMenuNamesOptions = {},
) => {
  const topicId = useTopicId(topicIdParam);
  const menu = useGlobalOrTopicMenu(topicId);

  // Get the names of the pages from the menu.
  const { pageNames, namesToFetch } = getPageNamesFromMenu(
    menu ?? [],
    identifiers,
    Boolean(topicId),
  );

  // Fetch missing names of the pages from the page_menu_names endpoint.
  const { isLoading, data: fetchedPageNames } =
    usePageNamesQueries(namesToFetch);

  return useMemo(() => {
    // Return empty strings for unknown pages while loading.
    if (isLoading) {
      return fillUnknownWithEmptyStrings(pageNames);
    }

    const consolidatedNames = {
      ...pageNames,
      ...fetchedPageNames,
    };

    // If any page is still missing, use the default name.
    return fillUnknownWithDefaultNames(consolidatedNames, defaultNames);
  }, [pageNames, fetchedPageNames, defaultNames, isLoading]);
};

// Re-export the page identifiers for convenience.
export { PAGES } from 'hb-react/shared/constants/pageIdentifiers';
